import React, { createContext, useContext } from 'react';

interface TabsContextValue {
  value: string;
  onChange: (value: string) => void;
}

const TabsContext = createContext<TabsContextValue | null>(null);

function useTabsContext(): TabsContextValue {
  const ctx = useContext(TabsContext);
  if (!ctx) {
    throw new Error('Tabs components must be used within a Tabs provider');
  }
  return ctx;
}

interface TabsProps {
  value: string;
  onChange: (value: string) => void;
  children: React.ReactNode;
  className?: string;
}

function Tabs({ value, onChange, children, className = '' }: TabsProps) {
  const ctx: TabsContextValue = { value, onChange };
  return (
    <TabsContext.Provider value={ctx}>
      <div className={className || undefined}>{children}</div>
    </TabsContext.Provider>
  );
}

Tabs.displayName = 'Tabs';

interface TabListProps {
  children: React.ReactNode;
  className?: string;
}

function TabList({ children, className = '' }: TabListProps) {
  return (
    <div
      className={`flex items-center gap-1 px-6 py-2 border-b border-subtle ${className}`.trim()}
      role="tablist"
    >
      {children}
    </div>
  );
}

TabList.displayName = 'TabList';

interface TabProps {
  value: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

function Tab({ value, icon, children, className = '' }: TabProps) {
  const { value: activeValue, onChange } = useTabsContext();
  const isActive = activeValue === value;

  const baseClass =
    'flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors cursor-pointer outline-none';
  const activeClass = isActive
    ? 'bg-status-success/10 text-status-success'
    : 'text-content-tertiary hover:text-content-secondary hover:bg-surface-primary';

  return (
    <button
      type="button"
      role="tab"
      aria-selected={isActive}
      aria-controls={`tabpanel-${value}`}
      id={`tab-${value}`}
      className={`${baseClass} ${activeClass} ${className}`.trim()}
      onClick={() => onChange(value)}
    >
      {icon}
      {children}
    </button>
  );
}

Tab.displayName = 'Tab';

interface TabPanelProps {
  value: string;
  children: React.ReactNode;
  className?: string;
}

function TabPanel({ value, children, className = '' }: TabPanelProps) {
  const { value: activeValue } = useTabsContext();

  if (activeValue !== value) {
    return null;
  }

  return (
    <div
      id={`tabpanel-${value}`}
      role="tabpanel"
      aria-labelledby={`tab-${value}`}
      className={className || undefined}
    >
      {children}
    </div>
  );
}

TabPanel.displayName = 'TabPanel';

export { Tabs, TabList, Tab, TabPanel };
export type { TabsProps, TabListProps, TabProps, TabPanelProps };
